<!-- Мини-карточка ожидаемого маршрута рейса для компактных блоков -->
<template>
  <div
    class="trip-route-preview-card"
    :class="{ 'trip-route-preview-card--vertical': vertical }"
  >
    <div class="trip-route-preview-card__info">
      <span class="trip-route-preview-card__title">
        Маршрут
      </span>
      <template v-if="summaryReady">
        <span class="trip-route-preview-card__row">
          <span class="trip-route-preview-card__metric-label">Км</span>
          <span class="trip-route-preview-card__value">{{ distanceText }}</span>
        </span>
        <span class="trip-route-preview-card__row">
          <span class="trip-route-preview-card__metric-label">Время</span>
          <span class="trip-route-preview-card__value">{{ durationText }}</span>
        </span>
      </template>
      <span v-else class="trip-route-preview-card__muted trip-route-preview-card__muted--empty">
        Нет расчёта
      </span>
      <span class="trip-route-preview-card__muted">{{ legsText }}</span>
    </div>

    <div
      class="trip-route-preview-card__map-block"
      role="button"
      tabindex="0"
      aria-label="Открыть маршрут рейса"
      @click="$emit('open')"
      @keydown.enter.prevent="$emit('open')"
      @keydown.space.prevent="$emit('open')"
    >
      <TripRouteMap
        :route-legs="routeLegs"
        compact
        @refresh="generateRoute"
      />
    </div>
  </div>
</template>

<script>
import tripRoutesApi from "@/services/api/tripRoutesApi";

import TripRouteMap from "@/components/Trip/TripRouteMap.vue";

export default {
  name: "TripRoutePreviewCard",

  components: {
    TripRouteMap,
  },

  props: {
    tripId: {
      type: [Number, String],
      required: true,
    },
    companyId: {
      type: [Number, String],
      required: true,
    },
    vertical: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["open"],

  data() {
    return {
      routeLegs: [],
      tripSummary: null,
    };
  },

  computed: {
    distanceText() {
      if (!this.summaryReady) return "";

      return this.formatDistance(this.tripSummary.total_distance_km);
    },

    durationText() {
      if (!this.summaryReady) return "";

      return this.formatDuration(this.tripSummary.total_time_minutes);
    },

    summaryReady() {
      return (
        this.tripSummary &&
        this.tripSummary.total_distance_km != null &&
        this.tripSummary.total_time_minutes != null
      );
    },

    legsText() {
      const count = this.routeLegs.length;
      if (count === 0) return "Участки не построены";

      return `${count} ${this.legsCountLabel(count)}`;
    },
  },

  created() {
    this.loadRouteData();
  },

  methods: {
    async loadRouteData() {
      try {
        const [legsResponse, summariesResponse] = await Promise.all([
          tripRoutesApi.index(this.companyId, this.tripId),
          tripRoutesApi.summariesIndex(this.companyId, this.tripId),
        ]);

        this.routeLegs = legsResponse.data.result || [];
        this.tripSummary = this.firstSummary(summariesResponse.data.result);
      } catch (error) {
        console.error("Ошибка при загрузке маршрута рейса:", error);
      }
    },

    async generateRoute() {
      try {
        await tripRoutesApi.generateSummaries(this.companyId, this.tripId);
        await this.loadRouteData();
      } catch (error) {
        console.error("Ошибка при пересчете маршрута рейса:", error);
      }
    },

    firstSummary(result) {
      return Array.isArray(result) ? result[0] : result;
    },

    formatDistance(km) {
      return `${Number(km).toFixed(1)} км`;
    },

    formatDuration(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours === 0) return `${mins} мин`;

      return `${hours} ч ${mins} мин`;
    },

    legsCountLabel(count) {
      const lastTwoDigits = count % 100;
      const lastDigit = count % 10;

      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return "участков";
      if (lastDigit === 1) return "участок";
      if (lastDigit >= 2 && lastDigit <= 4) return "участка";

      return "участков";
    },
  },
};
</script>

<style scoped>
.trip-route-preview-card {
  display: grid;
  grid-template-columns: minmax(132px, 0.75fr) minmax(210px, 1.25fr);
  gap: 18px;
  align-items: stretch;
  min-width: 0;
  height: 100%;
}

.trip-route-preview-card--vertical {
  grid-template-columns: minmax(132px, 0.75fr) minmax(210px, 1.25fr);
  min-height: 144px;
}

.trip-route-preview-card__info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  min-width: 0;
  padding: 0;
  text-align: left;
}

.trip-route-preview-card__title {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
}

.trip-route-preview-card__row {
  display: grid;
  grid-template-columns: 48px minmax(72px, 1fr);
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.trip-route-preview-card__metric-label {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.1;
  text-align: left;
  text-transform: uppercase;
}

.trip-route-preview-card__value {
  color: #1f2937;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  text-align: right;
  white-space: nowrap;
}

.trip-route-preview-card__muted {
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.2;
}

.trip-route-preview-card__muted--empty {
  padding: 9px 7px;
  background: #fff;
  border: 1px dashed #e2e8f0;
  border-radius: 6px;
}

.trip-route-preview-card__map-block {
  height: 100%;
  min-width: 0;
  min-height: 120px;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
}

.trip-route-preview-card__map-block:hover {
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.14);
}

@media (max-width: 520px) {
  .trip-route-preview-card {
    grid-template-columns: 1fr;
    height: auto;
  }

  .trip-route-preview-card__map-block {
    height: 132px;
  }
}
</style>
